import { useEffect, useState } from "react";
import { ApiError } from "../lib/api";
import { Button, Field, Segmented } from "../components/rumonai";
import { Icon } from "../components/Icon";
import { useBusyAction } from "../lib/busy";
import { AuthShell, PasswordField, SubmitButton } from "./AuthShell";
import {
  announceAccountChange,
  hub,
  loginDestination,
  type Account,
} from "./api";

/**
 * The ways in: a password; an invite; a reset link mailed to the address on the
 * account; or a reset code an admin issued, for an account with no address.
 */
type Mode = "login" | "signup" | "forgot" | "reset";

const words: Record<Mode, { title: string; lead: string; submit: string; doing: string }> = {
  login: { title: "登录涟漪", lead: "继续和你的 IP 一起工作。", submit: "登录", doing: "正在登录…" },
  signup: { title: "加入涟漪", lead: "填上邀请码，起个名字就能开始。", submit: "创建账号", doing: "正在创建…" },
  forgot: { title: "找回密码", lead: "填写账号名或邮箱，重置链接会发到账号的邮箱。", submit: "发送重置链接", doing: "正在发送…" },
  reset: { title: "设置新密码", lead: "用管理员给你的重置码设置新密码，然后进入。", submit: "设置新密码并进入", doing: "正在设置…" },
};

/** The code in an invite link (`/#/join/<code>`), or "". It stays in the address fragment, which no server sees. */
function inviteInAddress(): string {
  const found = /^#\/join\/([^/?#]+)/.exec(location.hash);
  if (!found) return "";
  try { return decodeURIComponent(found[1]); } catch { return ""; }
}

const looksLikeAddress = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export function HubAuth({
  onAccount,
}: {
  onAccount: (account: Account) => void;
}) {
  // Whether this Hub can send a mail at all: what the address is for is only said when a mail can come of it.
  // Unknown until the Hub answers, and then nothing is promised.
  const [mail, setMail] = useState<boolean | null>(null);
  useEffect(() => {
    const abort = new AbortController();
    hub.mailReady(abort.signal).then(ready => { if (!abort.signal.aborted) setMail(ready); }, () => {});
    return () => abort.abort();
  }, []);
  const [linked, setLinked] = useState(inviteInAddress);
  const [mode, setMode] = useState<Mode>(() => linked ? "signup" : location.hash.split("?")[0] === "#/forgot" ? "forgot" : "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [who, setWho] = useState("");
  const [password, setPassword] = useState("");
  const [hidden, setHidden] = useState(true);
  const [invite, setInvite] = useState(linked);
  const [code, setCode] = useState("");
  // One press, one request: the lock is taken on the press, not on state that
  // lands a frame later, so a double-click sends the form once.
  const { busy, run } = useBusyAction(300);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  // Signing up and a reset both set a password, so both hold it to the rule.
  const settingPassword = mode === "signup" || mode === "reset";
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (mode === "signup" && !looksLikeAddress(email)) {
      setEmailError("这个邮箱的格式不对，请检查后再填。");
      return;
    }
    // The refusal is drawn when the button is free again, so the form never
    // says "refused" while it still looks like it is working.
    void run(async () => {
      if (mode === "forgot") {
        await hub.forgotPassword(who.trim());
        setSent(true);
        return;
      }
      const result =
        mode === "login"
          ? await hub.login(name.trim(), password)
          : mode === "signup"
            ? await hub.signup(name.trim(), password, invite.trim(), email.trim())
            : await hub.resetPassword(name.trim(), code.trim(), password);
      setPassword("");
      setHidden(true);
      setInvite("");
      setCode("");
      announceAccountChange();
      if (linked) history.replaceState(null, "", location.pathname + location.search);
      const next = loginDestination(location.search, location.hash);
      if (next) location.assign(next);
      else onAccount(result.account);
    }).catch(failure => setError(ApiError.fromUnknown(failure)));
  };
  // An invite opened in a tab that is already on this page changes only the address: the page follows it
  // to creating the account, with the code filled in, as it does for a link opened fresh.
  useEffect(() => {
    const follow = () => {
      const opened = inviteInAddress();
      if (!opened) return;
      if (mode === "reset") setPassword("");
      setLinked(opened);
      setInvite(opened);
      setMode("signup");
      setHidden(true);
      setError(null);
      setEmailError("");
      setSent(false);
    };
    window.addEventListener("hashchange", follow);
    return () => window.removeEventListener("hashchange", follow);
  }, [mode]);
  const selectMode = (next: Mode) => {
    if (busy) return;
    // A new password typed for a reset is not the one to sign in with, nor
    // the other way round.
    if (next === "reset" || mode === "reset") setPassword("");
    // The name already typed is the one being looked for.
    if (next === "forgot" && !who) setWho(name);
    setMode(next);
    setHidden(true);
    setError(null);
    setEmailError("");
    setSent(false);
  };
  const ready =
    mode === "forgot" ? Boolean(who.trim())
    : Boolean(name.trim() && password)
      && (mode !== "signup" || Boolean(invite.trim() && email.trim()))
      && (mode !== "reset" || Boolean(code.trim()));
  // With no mail service a reset link cannot be sent: the page says so and leads to the admin's code instead of a form that cannot work.
  const noLink = mode === "forgot" && mail === false;
  // An invite that came in a link is already filled in, and the page says so where it speaks to the person.
  const lead = noLink ? "邮件服务还没有开通，重置链接发不出去。请联系管理员，要一个重置码。"
    : mode === "signup" && linked && invite === linked ? "朋友的邀请码已经填好，起个名字就能开始。" : words[mode].lead;
  return (
    <AuthShell title={sent ? "查看你的邮箱" : words[mode].title} lead={sent ? undefined : lead}>
      {/* A reset is neither of the two tabs; its way back is 返回登录 below. */}
      {mode === "login" || mode === "signup" ? <Segmented className="auth-tabs" label="账号操作" value={mode}
        onValueChange={value => selectMode(value as Mode)} disabled={busy}
        items={[{ value: "login", label: "登录" }, { value: "signup", label: "注册" }]} /> : null}
      {sent ? <div className="auth-sent pa-reveal" role="status">
        <p><Icon name="done" size={18} />如果这个账号有已验证的邮箱，重置链接已经发过去了</p>
        <small>链接 30 分钟内有效，只能用一次。没有收到的话，看看垃圾邮件。</small>
      </div> : noLink ? <div className="auth-form" data-mode={mode}>
        <Button type="button" className="auth-submit" name="use-code" onClick={() => selectMode("reset")}>
          <span>用管理员给的重置码</span><Icon name="arrow" size={18} />
        </Button>
      </div> : <form className="auth-form" onSubmit={submit} aria-busy={busy} data-mode={mode} noValidate>
        {mode === "signup" ? <Field className="auth-invite" label="邀请码">
          <input autoComplete="off" placeholder="邀请人给你的那一串" value={invite}
            onChange={(event) => setInvite(event.target.value)} required disabled={busy} />
        </Field> : null}
        {mode === "forgot" ? <Field label="账号名或邮箱">
          <input name="who" autoComplete="username" autoCapitalize="none" spellCheck={false}
            placeholder="账号名，或注册时填的邮箱地址" value={who}
            onChange={(event) => setWho(event.target.value)} required disabled={busy} autoFocus />
        </Field> : <Field label="账号名">
          <input name="username" autoComplete="username" placeholder={mode === "signup" ? "字母或数字，登录时用" : "登录时用的账号名"} value={name}
            onChange={(event) => setName(event.target.value)} required disabled={busy} />
        </Field>}
        {mode === "signup" ? <Field className="auth-email" label="邮箱" id="auth-email" error={emailError || undefined}
          hint={<span data-unknown={mail === null ? "" : undefined} aria-hidden={mail === null ? true : undefined}>
            {mail === false ? "邮箱先记着。邮件服务还没有开通，暂时收不到邮件。" : "忘记密码时，重置链接会发到这里。"}</span>}>
          <input name="email" type="email" inputMode="email" autoComplete="email" autoCapitalize="none"
            spellCheck={false} placeholder="name@example.com" value={email} aria-invalid={emailError ? true : undefined}
            onChange={(event) => { setEmail(event.target.value); setEmailError(""); }} required disabled={busy} />
        </Field> : null}
        {mode === "reset" ? <Field className="auth-code" label="重置码">
          <input name="code" autoComplete="one-time-code" autoCapitalize="characters" spellCheck={false}
            placeholder="管理员发给你的重置码" value={code}
            onChange={(event) => setCode(event.target.value)} required disabled={busy} />
        </Field> : null}
        {mode === "forgot" ? null : <PasswordField label={mode === "reset" ? "新密码" : "密码"}
          placeholder={settingPassword ? "至少 8 位" : "你设置的密码"} value={password} onChange={setPassword}
          autoComplete={settingPassword ? "new-password" : "current-password"} setting={settingPassword}
          disabled={busy} hidden={hidden} onHide={setHidden} />}
        {mode === "login" ? <Button type="button" variant="ghost" className="auth-forgot" name="forgot" disabled={busy}
          onClick={() => selectMode("forgot")}>忘记密码</Button> : null}
        <div className="auth-form-actions">
          <div className="auth-feedback" role="alert" aria-atomic="true">
            {error ? <div className="pa-feedback"><strong>{error.title}</strong><p>{error.detail}{error.hint ? ` ${error.hint}` : ""}</p></div> : null}
          </div>
          <SubmitButton busy={busy} doing={words[mode].doing} disabled={!ready}>{words[mode].submit}</SubmitButton>
        </div>
      </form>}
      <footer className="auth-switch">
        {mode === "forgot" || mode === "reset" ? <>
          <span>想起密码了？</span>
          <Button type="button" variant="ghost" name="back" onClick={() => selectMode("login")} disabled={busy}>返回登录</Button>
          {mode === "forgot" && !sent && !noLink ? <Button type="button" variant="ghost" className="auth-alt" name="use-code" onClick={() => selectMode("reset")}
            disabled={busy}>用管理员给的重置码</Button> : null}
        </> : <>
          <span>{mode === "login" ? "还没有账号？" : "已经有账号？"}</span>
          <Button type="button" variant="ghost" onClick={() => selectMode(mode === "login" ? "signup" : "login")} disabled={busy}>
            {mode === "login" ? "使用邀请码加入" : "登录"}
          </Button>
        </>}
      </footer>
    </AuthShell>
  );
}
